import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import type { SidebarNavGroup, SidebarNavItem } from './sidebar-nav-item.interface'
import { SidebarNavItemWithMenu } from './sidebar-nav-item-with-menu.molecule'

interface NavItemRenderProps {
  item: SidebarNavItem
  group: SidebarNavGroup
  href: string
}

const renderNavItemWithMenu = ({ item, group, href }: NavItemRenderProps): ReactNode => {
  if (!item.id) return null

  if (group.itemType === 'chat' && group.chatActions) {
    return (
      <SidebarNavItemWithMenu
        key={item.path}
        id={item.id}
        title={item.title}
        href={href}
        icon={item.icon}
        isActive={item.isActive}
        itemType="chat"
        isPinned={item.isPinned}
        actions={group.chatActions}
        editState={group.chatEditState}
      />
    )
  }

  if (group.itemType === 'project' && group.projectActions) {
    return (
      <SidebarNavItemWithMenu
        key={item.path}
        id={item.id}
        title={item.title}
        href={href}
        icon={item.icon}
        isActive={item.isActive}
        itemType="project"
        actions={group.projectActions}
      />
    )
  }

  if (group.itemType === 'agent' && group.agentActions) {
    return (
      <SidebarNavItemWithMenu
        key={item.path}
        id={item.id}
        title={item.title}
        href={href}
        icon={item.icon}
        isActive={item.isActive}
        itemType="agent"
        isPinned={item.isPinned}
        actions={group.agentActions}
      />
    )
  }

  return null
}

interface SidebarNavGroupItemsProps {
  group: SidebarNavGroup
  visibleItemList: SidebarNavItem[]
  buildUrl: (path: string) => string
}

export const SidebarNavGroupItems = ({
  group,
  visibleItemList,
  buildUrl,
}: SidebarNavGroupItemsProps): ReactNode => (
  <>
    {visibleItemList.map((item) => {
      const menuItem = renderNavItemWithMenu({
        item,
        group,
        href: buildUrl(item.path),
      })

      if (menuItem) return menuItem

      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton asChild isActive={item.isActive} tooltip={item.title}>
            <Link to={buildUrl(item.path)}>
              <item.icon />
              <span>{item.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )
    })}
  </>
)

interface SidebarNavGroupToggleProps {
  isExpanded: boolean
  hiddenCount: number
  onToggle: () => void
}

export const SidebarNavGroupToggle = ({
  isExpanded,
  hiddenCount,
  onToggle,
}: SidebarNavGroupToggleProps): ReactNode => (
  <SidebarMenuItem>
    <SidebarMenuButton onClick={onToggle} className="text-muted-foreground hover:text-foreground">
      {isExpanded ? (
        <>
          <ChevronUpIcon />
          <span>Show less</span>
        </>
      ) : (
        <>
          <ChevronDownIcon />
          <span>See all ({hiddenCount} more)</span>
        </>
      )}
    </SidebarMenuButton>
  </SidebarMenuItem>
)

interface SidebarNavGroupActionProps {
  icon: React.ComponentType
  tooltip: string
  onClick: () => void
}

export const SidebarNavGroupAction = ({
  icon: Icon,
  tooltip,
  onClick,
}: SidebarNavGroupActionProps): ReactNode => (
  <Tooltip>
    <TooltipTrigger asChild>
      <SidebarGroupAction onClick={onClick}>
        <Icon />
        <span className="sr-only">{tooltip}</span>
      </SidebarGroupAction>
    </TooltipTrigger>
    <TooltipContent side="right">{tooltip}</TooltipContent>
  </Tooltip>
)

interface SidebarNavGroupContainerProps {
  group: SidebarNavGroup
  visibleItemList: SidebarNavItem[]
  isExpanded: boolean
  isCollapsed: boolean
  showToggle: boolean
  hiddenCount: number
  buildUrl: (path: string) => string
  onToggle: () => void
}

export const SidebarNavGroupContainer = ({
  group,
  visibleItemList,
  isExpanded,
  isCollapsed,
  showToggle,
  hiddenCount,
  buildUrl,
  onToggle,
}: SidebarNavGroupContainerProps): ReactNode => (
  <SidebarGroup key={group.label}>
    <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
    {group.action && (
      <SidebarNavGroupAction
        icon={group.action.icon}
        tooltip={group.action.tooltip}
        onClick={group.action.onClick}
      />
    )}
    <SidebarGroupContent>
      <SidebarMenu>
        <SidebarNavGroupItems group={group} visibleItemList={visibleItemList} buildUrl={buildUrl} />
        {showToggle && !isCollapsed && (
          <SidebarNavGroupToggle
            isExpanded={isExpanded}
            hiddenCount={hiddenCount}
            onToggle={onToggle}
          />
        )}
      </SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
)
